import {
	EmptyState,
	PageHeader,
	PaginationPageDefault,
	TableCard,
} from "@components";
import { Button } from "@components/button/button-untitledui";
import { usePaginationTotal } from "@components/pagination/use-pagination-total";
import { FleeterIcon } from "@features/copilot/components";
import { CalendarDate, getLocalTimeZone, today } from "@internationalized/date";
import { useCallback, useMemo, useState } from "react";
import type { DateValue } from "react-aria";
import type { SortDescriptor } from "react-aria-components";
import { RankingModeToggle } from "@/shared/components/ranking-mode-toggle";
import { useDebouncedCommit } from "@/shared/hooks/use-debounced-commit";
import { ListingDataTable } from "../components/listing-data-table";
import { RankingFilters } from "../components/ranking-filters";
import {
	RANKING_BY_DRIVER_COLUMNS,
	RANKING_BY_VEHICLE_COLUMNS,
	RANKING_SORT_BY_MAP,
} from "../constants/ranking";
import { useRankingList } from "../hooks/use-ranking-list";
import {
	type RankingMode,
	renderRankingCell,
	renderRankingDetailRow,
} from "../utils/ranking-render";

const PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 500;

const normalizeSearch = (value: string) => value.trim();

type RankingFiltersState = {
	search: string;
	startDate: DateValue;
	endDate: DateValue;
	isDriver: boolean;
};

function createDefaultFilters(): RankingFiltersState {
	const now = today(getLocalTimeZone());
	const startDate = new CalendarDate(now.year, now.month, 1);
	const lastDay = new Date(now.year, now.month, 0).getDate();
	const endDate = new CalendarDate(now.year, now.month, lastDay);
	return {
		search: "",
		startDate,
		endDate,
		isDriver: false,
	};
}

function hasActiveRankingFilters(filters: RankingFiltersState): boolean {
	const def = createDefaultFilters();
	if (filters.search.trim() !== def.search.trim()) {
		return true;
	}
	if (filters.startDate.toString() !== def.startDate.toString()) {
		return true;
	}
	if (filters.endDate.toString() !== def.endDate.toString()) {
		return true;
	}
	return false;
}

export default function TrackingRankingPage() {
	const [page, setPage] = useState(1);
	const [filters, setFilters] =
		useState<RankingFiltersState>(createDefaultFilters);
	const [searchInput, setSearchInput] = useState("");
	const [sortDescriptor, setSortDescriptor] = useState<
		SortDescriptor | undefined
	>();

	const patchFilters = useCallback((patch: Partial<RankingFiltersState>) => {
		setFilters((prev) => {
			const next = { ...prev, ...patch };
			const startDate = next.startDate;
			const endDate = next.endDate;
			const todayDate = today(getLocalTimeZone());
			if (endDate.toString() < startDate.toString()) {
				return { ...next, endDate: startDate };
			}
			if (endDate.toString() > todayDate.toString()) {
				return { ...next, endDate: todayDate };
			}
			return next;
		});
		setPage(1);
	}, []);

	const applySearch = useCallback(
		(term: string) => {
			patchFilters({ search: term });
		},
		[patchFilters]
	);

	useDebouncedCommit({
		value: searchInput,
		applied: filters.search,
		delay: SEARCH_DEBOUNCE_MS,
		normalize: normalizeSearch,
		onCommit: applySearch,
	});

	const handleClear = useCallback(() => {
		setSearchInput("");
		setFilters((prev) => {
			const defaults = createDefaultFilters();
			return {
				...prev,
				search: defaults.search,
				startDate: defaults.startDate,
				endDate: defaults.endDate,
			};
		});
		setPage(1);
	}, []);

	const handleModeChange = useCallback((isDriver: boolean) => {
		setFilters((prev) => ({ ...prev, isDriver }));
		setPage(1);
	}, []);

	const handleSortChange = useCallback((descriptor: SortDescriptor) => {
		setSortDescriptor(descriptor);
		setPage(1);
	}, []);

	const columns = useMemo(
		() =>
			filters.isDriver ? RANKING_BY_DRIVER_COLUMNS : RANKING_BY_VEHICLE_COLUMNS,
		[filters.isDriver]
	);

	const sortBy = sortDescriptor
		? RANKING_SORT_BY_MAP[String(sortDescriptor.column)]
		: undefined;

	const criteria = useMemo(
		() => ({
			startDate: filters.startDate.toString(),
			endDate: filters.endDate.toString(),
			isDriver: filters.isDriver,
			...(filters.search.trim() && { search: filters.search.trim() }),
			pageSize: PAGE_SIZE,
			...(sortBy && {
				sortBy,
				sortDirection: (sortDescriptor?.direction === "descending"
					? "desc"
					: "asc") as "asc" | "desc",
			}),
		}),
		[
			filters.startDate,
			filters.endDate,
			filters.isDriver,
			filters.search,
			sortBy,
			sortDescriptor?.direction,
		]
	);

	const queryParams = useMemo(
		() => ({ ...criteria, pageNumber: page }),
		[criteria, page]
	);

	const rankingQuery = useRankingList(queryParams);
	const { data, isLoading, isError, refetch } = rankingQuery;

	const { total: totalPages } = usePaginationTotal({
		page,
		onPageChange: setPage,
		criteria,
		totalPages: data?.totalPages,
		query: rankingQuery,
	});

	const items = data?.items ?? [];
	const showPagination = totalPages !== null && totalPages > 1;
	const showClearFilters =
		!isError && items.length > 0 && hasActiveRankingFilters(filters);
	const mode: RankingMode = filters.isDriver ? "driver" : "vehicle";
	const ariaLabel = filters.isDriver
		? "Ranking de motoristas"
		: "Ranking de veículos";

	const emptyContent = (
		<EmptyState className="w-full" size="md">
			<EmptyState.Header pattern="none">
				<EmptyState.FeaturedIcon color="gray" theme="modern" />
			</EmptyState.Header>
			<EmptyState.Content>
				<EmptyState.Title>Nenhum registro encontrado</EmptyState.Title>
				<EmptyState.Description>
					{filters.isDriver
						? "Não localizamos motoristas para o período e filtros selecionados."
						: "Não localizamos veículos para o período e filtros selecionados."}
				</EmptyState.Description>
			</EmptyState.Content>
			<EmptyState.Footer className="justify-center">
				<Button color="secondary" onClick={handleClear} size="sm">
					Limpar filtros
				</Button>
			</EmptyState.Footer>
		</EmptyState>
	);

	return (
		<div className="flex flex-col gap-6 px-6 py-8">
			<PageHeader title="Ranking" />

			<RankingFilters
				endDate={filters.endDate}
				onClear={handleClear}
				onEndDateChange={(date) => {
					patchFilters({ endDate: date });
				}}
				onSearchInputChange={setSearchInput}
				onStartDateChange={(date) => {
					patchFilters({ startDate: date });
				}}
				searchInput={searchInput}
				showClearFilters={showClearFilters}
				startDate={filters.startDate}
			/>

			<TableCard.Root variant="listing">
				<div className="border-secondary border-b bg-primary px-4 py-5 md:px-6">
					<div className="flex flex-col items-start gap-4">
						<div className="flex w-full flex-col gap-0.5">
							<h2 className="font-semibold text-lg text-primary">
								Desempenho operacional
							</h2>
							<p className="text-sm text-tertiary">
								Avaliação de performance da frota
							</p>
						</div>
						<div className="flex w-full flex-wrap items-center justify-between gap-4">
							<RankingModeToggle
								isDriver={filters.isDriver}
								onModeChange={handleModeChange}
							/>
							<Button
								color="secondary"
								href="/copilot/summary"
								iconLeading={
									<span aria-hidden className="flex shrink-0">
										<FleeterIcon className="text-[#0080FE]" size={14} />
									</span>
								}
								size="sm"
							>
								Sugestões do Copiloto
							</Button>
						</div>
					</div>
				</div>
				<ListingDataTable
					ariaLabel={ariaLabel}
					columns={columns}
					density="compact"
					emptyContent={emptyContent}
					errorMessage="Não foi possível carregar o ranking."
					expandable
					getRowKey={(item, index) => {
						if (filters.isDriver) {
							return `${item.cpf ?? ""}|${item.name ?? ""}|${index}`;
						}
						return `${item.plate ?? ""}|${index}`;
					}}
					isError={isError}
					isLoading={isLoading}
					items={items}
					loadingLabel="Carregando ranking"
					onRetry={() => {
						refetch();
					}}
					onSortChange={handleSortChange}
					renderCell={(item, col, colIndex) =>
						renderRankingCell(item, col, colIndex, mode)
					}
					renderExpandedRow={renderRankingDetailRow}
					sortDescriptor={sortDescriptor}
					variant="listing"
				/>
				{showPagination ? (
					<PaginationPageDefault
						className="p-5"
						onPageChange={setPage}
						page={page}
						total={totalPages}
					/>
				) : null}
			</TableCard.Root>
		</div>
	);
}
